import { useState } from 'react';

/**
 * Ali assistant panel — collapsible on-page command list for the shared
 * Result page. Mirrors the Create-page AliCommands vocabulary but here
 * the items are *narrative commands describing what Ali did* (not
 * interactive buttons — the demo is shared read-only). Each command
 * explains a specific decision Ali made when building the demo.
 *
 * Real Ali (MAPTRIUM proper) uses Claude Sonnet 4.6 tool-use; this
 * variant is static so the shared link is instant + free + offline.
 */

const QA_BY_TYPE = {
  crash_safety: [
    {
      q: 'What am I looking at?',
      a: 'Three years of crash incidents in the selected radius, colored by severity. Red = fatal/severe, orange = injury, yellow = property damage. Shaded polygons are DBSCAN-derived hotspots — where crashes cluster tighter than random chance.',
    },
    {
      q: 'How do I use this for a grant?',
      a: 'Export the PDF and pair it with the hotspot statistics on the right. For SS4A (Safe Streets for All) or HSIP grants, the hotspot count + fatal count in this view is exactly what the application form asks for. In production MAPTRIUM, we auto-fill the SS4A narrative from these numbers.',
    },
    {
      q: 'Is the data real?',
      a: 'This is synthetic sample data, seeded per demo so the map is reproducible. In production MAPTRIUM, we ingest TREDS (or your state DOT feed) directly. Ask for "Request full analysis" and we\'ll rerun this with your actual data in under 24 hours.',
    },
    {
      q: 'What about pedestrians and bikes?',
      a: 'Not shown in this preview. Full MAPTRIUM breaks out pedestrian + bicycle incidents separately, applies the FHWA PHB warrant test, and flags corridors that qualify for federal pedestrian-crossing funding.',
    },
  ],
  event_ops: [
    {
      q: 'What am I looking at?',
      a: 'A permitted-event footprint with gate positions, medical stations, security posts, and vendor zones. Sized for a mid-scale festival or public gathering.',
    },
    {
      q: 'Can I edit gate positions?',
      a: 'Not in this demo — positions are seeded. Production MAPTRIUM has drag-to-edit for every asset, exports to TAK for on-site coordination, and pushes to AGOL for incident-command dashboards.',
    },
    {
      q: 'What does the medical capacity number mean?',
      a: 'Total treatment capacity across all medical stations combined. A rough planning heuristic is 1 seat per 100 attendees expected. Production MAPTRIUM lets you set the attendee model + auto-sizes stations.',
    },
    {
      q: 'How do first responders get this?',
      a: 'Ops teams typically get a printed PDF + a TAK feed. Production MAPTRIUM pushes to TAK Server in ~2 seconds after publish; PDF matches the layout your county EOC expects.',
    },
  ],
  permit_row: [
    {
      q: 'What am I looking at?',
      a: 'A project right-of-way corridor with affected parcels along its length, permit application markers, and a utility easement. Used for pre-construction outreach and permit tracking.',
    },
    {
      q: 'Are parcel IDs real?',
      a: 'Not in the demo — placeholder IDs. Production MAPTRIUM ingests county parcel layers directly (any ArcGIS Feature Service or shapefile). We keep owner-of-record data separate for privacy.',
    },
    {
      q: 'Can I share this with landowners?',
      a: 'Yes — the share link is public-read, expires 30 days by default. Production MAPTRIUM adds per-parcel outreach letters + digital signature capture.',
    },
    {
      q: 'What permits are shown?',
      a: 'Sample permit points with pending / approved status. Production MAPTRIUM syncs directly with Accela or Tyler ePermit systems (whatever your county uses).',
    },
  ],
  emergency_response: [
    {
      q: 'What am I looking at?',
      a: 'Emergency Operations Center at the marked location, with surrounding fire/EMS stations, hospitals (Level I trauma highlighted), shelter capacity, and the active hazard footprint (flood or wildfire).',
    },
    {
      q: 'Why is my hospital on a road?',
      a: 'Points are snapped to the road network so they land on drivable surface, not water or greenspace. In production MAPTRIUM, we snap to the actual facility address from your CAD system.',
    },
    {
      q: 'Can I run a scenario?',
      a: 'Not in the demo. Production MAPTRIUM includes flood-depth modeling (2, 5, 10-year floodplains), wildfire spread simulation, and EMS drive-time isochrones you can toggle live.',
    },
    {
      q: 'How is this delivered to field teams?',
      a: 'PDF + TAK feed + AGOL WebMap. TAK gives field radios a live view; AGOL feeds the county EOC ArcGIS dashboards. Production MAPTRIUM pushes all three from one publish.',
    },
  ],
};

export default function AliPanel({ mapType, org }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);

  const qa = QA_BY_TYPE[mapType] || QA_BY_TYPE.crash_safety;
  const current = qa[selected];

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-atlas-paper transition-colors"
        aria-expanded={open}
        aria-controls="ali-body"
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold"
            style={{ background: 'linear-gradient(135deg,#D4A437 0%,#8b6a1a 100%)' }}
          >
            A
          </span>
          <span className="text-sm font-semibold text-atlas-ink">Ali · commands</span>
          <span className="text-xs text-atlas-muted">— what MAPTRIUM's assistant did</span>
        </span>
        <span aria-hidden="true" className="text-atlas-muted text-lg leading-none">
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <div id="ali-body" className="px-4 pb-4 border-t border-atlas-line">
          <p className="text-xs text-atlas-muted mt-3 mb-2">
            Ali's decisions for {org || 'this project'}:
          </p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {qa.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSelected(i)}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                  i === selected
                    ? 'bg-atlas-navy text-white border-atlas-navy'
                    : 'bg-white text-atlas-ink border-atlas-line hover:border-atlas-navy'
                }`}
              >
                {item.q}
              </button>
            ))}
          </div>
          <div className="bg-atlas-paper rounded-md p-3">
            <p className="text-xs uppercase tracking-wider text-atlas-muted mb-1">Ali</p>
            <p className="text-sm text-atlas-ink leading-relaxed">{current.a}</p>
          </div>
          <p className="mt-3 text-[11px] text-atlas-muted">
            Static demo answers. In MAPTRIUM, Ali reads your actual data and cites sources live.
          </p>
        </div>
      )}
    </div>
  );
}
